import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CriarLeadInput {
  /**
   * Opcional: id da consulta em ecossistema_consultas para marcar virou_lead=true.
   * Quando ausente (ex.: persist falhou por PGRST205 ou similar), o lead ainda é
   * criado normalmente — apenas não há atualização cruzada na consulta.
   */
  consultaId?: string | null;
  cnpj: string;
  razaoSocial: string;
  uf?: string | null;
  telefone?: string | null;
  email?: string | null;
  nomeContato?: string | null;
  /**
   * Modo silencioso: usado pelo auto-vira-lead disparado em useEcossistemaApi
   * após cada consulta CNPJ. Suprime toast e navigate — apenas cria/atualiza o
   * lead em background.
   */
  silent?: boolean;
}

/**
 * Resolve (ou cria) a campanha mensal padrão "Ecossistema API — YYYY-MM" em
 * `campanhas_prospeccao`. Idempotente: se a campanha do mês já existir, reusa.
 */
export async function garantirCampanhaMensalEcossistemaApi(
  mesReferencia: string // formato YYYY-MM-01
): Promise<string> {
  const ano = mesReferencia.slice(0, 4);
  const mes = mesReferencia.slice(5, 7);
  const nomeCampanha = `ECOSSISTEMA API — ${ano}-${mes}`;

  const { data: existente } = await supabase
    .from('campanhas_prospeccao' as any)
    .select('id')
    .eq('nome', nomeCampanha)
    .eq('tipo_lead', 'ECOSSISTEMA_API' as any)
    .maybeSingle();

  if (existente?.id) return (existente as any).id as string;

  const { data: userRes } = await supabase.auth.getUser();
  const userId = userRes.user?.id;
  if (!userId) throw new Error('Usuário não autenticado');

  const { data: novaCampanha, error } = await supabase
    .from('campanhas_prospeccao' as any)
    .insert({
      nome: nomeCampanha,
      descricao:
        'Campanha mensal automática do Ecossistema API. Acolhe todos os leads gerados a partir de consultas (CNPJ enriquecido com Receita + Assertiva + Operadoras + Cobertura BL) durante o mês.',
      tipo_lead: 'ECOSSISTEMA_API' as any,
      origem: 'IMPORTACAO' as any,
      data_inicio: new Date().toISOString().split('T')[0],
      criada_por: userId,
      ativa: true,
    })
    .select('id')
    .single();

  if (error) throw error;
  return (novaCampanha as any).id as string;
}

/**
 * Função core PURA (sem hooks) para criar lead do Ecossistema. Pode ser
 * chamada de qualquer lugar — inclusive do auto-vira-lead disparado no
 * onSuccess do useEcossistemaApi após cada consulta CNPJ.
 *
 * REGRA CRM (constraints físicas no banco):
 * - idx_leads_campanha_cnpj_aberto = UNIQUE PARCIAL (campanha_id, cnpj)
 *   WHERE status_lead em aberto. → 1 lead por CNPJ POR CAMPANHA.
 * - idx_leads_carteira_cnpj_tipo_mes = UNIQUE (cnpj, tipo_lead,
 *   mes_referencia). → 1 lead por CNPJ no mesmo tipo+mês.
 *
 * IMPORTANTE: a regra é "1 lead por CNPJ POR CAMPANHA" — NÃO "1 lead por
 * CNPJ globalmente". Se o CNPJ já é lead em PORTABILIDADE_VIVO, isso NÃO
 * impede um lead novo em ECOSSISTEMA_API (campanha diferente, tipo
 * diferente). Cada campanha pode ter o mesmo CNPJ.
 *
 * COMPORTAMENTO:
 * - Já existe lead ECOSSISTEMA_API + mês atual + cnpj → UPSERT atualiza
 *   dados de contato (não duplica).
 * - Não existe → cria lead novo na campanha "Ecossistema API — YYYY-MM"
 *   independente do CNPJ ter leads em outras campanhas.
 */
export async function criarLeadEcossistemaCore(
  input: CriarLeadInput
): Promise<{
  leadId: string;
  importacaoId: string | null;
  campanhaId: string | null;
}> {
  console.info('[criarLeadEcossistemaCore] INÍCIO', { cnpj: input.cnpj, razaoSocial: input.razaoSocial });

  const cnpjClean = (input.cnpj || '').replace(/\D/g, '');
  if (cnpjClean.length !== 14) throw new Error('CNPJ inválido');

  const mesRef = new Date().toISOString().slice(0, 7) + '-01';

  // 1) Importação guarda-chuva do mês
  console.info('[criarLeadEcossistemaCore] passo 1 — importação mensal', { mesRef });
  let importacaoId: string | null = null;
  const { data: importExistente, error: errImportSelect } = await supabase
    .from('leads_carteira_importacoes' as any)
    .select('id')
    .eq('mes_referencia', mesRef)
    .maybeSingle();
  if (errImportSelect) {
    console.error('[criarLeadEcossistemaCore] passo 1a SELECT falhou:', errImportSelect);
  }

  if (importExistente?.id) {
    importacaoId = importExistente.id;
    console.info('[criarLeadEcossistemaCore] passo 1 — importação existente reutilizada', { importacaoId });
  } else {
    console.info('[criarLeadEcossistemaCore] passo 1 — criando nova importação');
    const { data: novaImport, error: errImport } = await supabase
      .from('leads_carteira_importacoes' as any)
      .insert({
        mes_referencia: mesRef,
        arquivo_nome: `ECOSSISTEMA API — ${mesRef.slice(0, 7)}`,
        status: 'CONCLUIDO',
        total_registros: 0,
      })
      .select('id')
      .single();
    if (errImport) {
      console.error('[criarLeadEcossistemaCore] passo 1b INSERT importacao FALHOU:', {
        code: (errImport as any).code,
        message: errImport.message,
        details: (errImport as any).details,
        hint: (errImport as any).hint,
      });
      throw errImport;
    }
    importacaoId = (novaImport as any).id;
    console.info('[criarLeadEcossistemaCore] passo 1 — nova importação criada', { importacaoId });
  }

  // 2) Campanha mensal padrão (best-effort — não bloqueia se falhar)
  console.info('[criarLeadEcossistemaCore] passo 2 — campanha mensal');
  let campanhaId: string | null = null;
  try {
    campanhaId = await garantirCampanhaMensalEcossistemaApi(mesRef);
    console.info('[criarLeadEcossistemaCore] passo 2 — campanha resolvida', { campanhaId });
  } catch (e) {
    console.error('[criarLeadEcossistemaCore] passo 2 — campanha FALHOU (não-fatal):', e);
  }

  // 3) UPSERT idempotente do lead em (cnpj, tipo_lead, mes_referencia)
  const leadRow: any = {
    cnpj: cnpjClean,
    razao_social: input.razaoSocial || `Cliente ${cnpjClean}`,
    uf: input.uf || 'BR',
    tipo_lead: 'ECOSSISTEMA_API',
    status_aptidao: 'APTO',
    status_lead: 'DISPONIVEL',
    importacao_id: importacaoId,
    mes_referencia: mesRef,
    telefone: input.telefone || null,
    contato_real_email: input.email || null,
    contato_real_nome: input.nomeContato || null,
    contato_real_telefone: input.telefone || null,
    contato_real_encontrado: !!(input.email || input.telefone || input.nomeContato),
    ...(campanhaId ? { campanha_id: campanhaId } : {}),
  };

  // Estratégia SELECT-then-INSERT-or-UPDATE em vez de UPSERT.
  // Motivo: ON CONFLICT (cnpj, tipo_lead, mes_referencia) retorna erro 42P10
  // ("no unique or exclusion constraint matching the ON CONFLICT
  // specification") — o índice idx_leads_carteira_cnpj_tipo_mes existe mas
  // não bate com a sintaxe do ON CONFLICT (provavelmente por causa do CAST
  // implícito do enum tipo_lead). Esta abordagem evita a dependência da
  // constraint nomeada e funciona em qualquer estado do schema.
  console.info('[criarLeadEcossistemaCore] passo 3a — SELECT lead existente', {
    cnpj: leadRow.cnpj,
    tipo_lead: leadRow.tipo_lead,
    mes_referencia: leadRow.mes_referencia,
  });
  const { data: leadExistente, error: errSelect } = await supabase
    .from('leads_carteira' as any)
    .select('id')
    .eq('cnpj', leadRow.cnpj)
    .eq('tipo_lead', leadRow.tipo_lead)
    .eq('mes_referencia', leadRow.mes_referencia)
    .maybeSingle();

  if (errSelect) {
    console.error('[criarLeadEcossistemaCore] passo 3a SELECT FALHOU:', {
      code: (errSelect as any).code,
      message: errSelect.message,
    });
    throw errSelect;
  }

  let leadId: string;
  if (leadExistente?.id) {
    // ── REGRA DO SISTEMA — NÃO SOBRESCREVE CONTATO REAL ────────────────
    // Lead JÁ existe (cnpj, tipo, mês). Re-consultar pelo Ecossistema NÃO
    // pode sobrescrever razao_social, telefone, contato_real_*, uf — o
    // consultor pode ter preenchido/corrigido esses campos manualmente.
    // APENAS o consultor altera dados do lead. O sistema só preenche
    // campos VAZIOS (NULL). Nunca substitui valores existentes.
    //
    // Buscamos o lead atual, calculamos um patch só com campos que
    // estavam NULL/vazios, e fazemos UPDATE apenas se há algo novo.
    console.info('[criarLeadEcossistemaCore] passo 3b — lead existe, calculando patch não-destrutivo', {
      leadId: leadExistente.id,
    });
    const { data: leadAtual, error: errSelectFull } = await supabase
      .from('leads_carteira' as any)
      .select('razao_social, uf, telefone, contato_real_email, contato_real_nome, contato_real_telefone, contato_real_encontrado')
      .eq('id', leadExistente.id)
      .single();
    if (errSelectFull) {
      console.error('[criarLeadEcossistemaCore] passo 3b SELECT atual FALHOU:', errSelectFull);
      throw errSelectFull;
    }
    const atual: any = leadAtual || {};
    const patch: any = {};
    const isVazio = (v: any) => v === null || v === undefined || String(v).trim() === '';
    if (isVazio(atual.razao_social) && leadRow.razao_social) patch.razao_social = leadRow.razao_social;
    if (isVazio(atual.uf) && leadRow.uf) patch.uf = leadRow.uf;
    if (isVazio(atual.telefone) && leadRow.telefone) patch.telefone = leadRow.telefone;
    if (isVazio(atual.contato_real_email) && leadRow.contato_real_email) patch.contato_real_email = leadRow.contato_real_email;
    if (isVazio(atual.contato_real_nome) && leadRow.contato_real_nome) patch.contato_real_nome = leadRow.contato_real_nome;
    if (isVazio(atual.contato_real_telefone) && leadRow.contato_real_telefone) patch.contato_real_telefone = leadRow.contato_real_telefone;
    // contato_real_encontrado vira true se algum dos campos foi preenchido AGORA
    if (Object.keys(patch).some((k) => k.startsWith('contato_real_'))) {
      patch.contato_real_encontrado = true;
    }

    if (Object.keys(patch).length === 0) {
      console.info('[criarLeadEcossistemaCore] passo 3b — nada a atualizar (lead já tem todos os dados — preservando consultor)');
    } else {
      console.info('[criarLeadEcossistemaCore] passo 3b — UPDATE só de campos vazios', { patch });
      const { error: errUpdate } = await supabase
        .from('leads_carteira' as any)
        .update(patch)
        .eq('id', leadExistente.id);
      if (errUpdate) {
        console.error('[criarLeadEcossistemaCore] passo 3b UPDATE FALHOU:', {
          code: (errUpdate as any).code,
          message: errUpdate.message,
          details: (errUpdate as any).details,
          hint: (errUpdate as any).hint,
        });
        throw errUpdate;
      }
    }
    leadId = leadExistente.id as string;
  } else {
    // INSERT novo
    console.info('[criarLeadEcossistemaCore] passo 3c — INSERT novo lead');
    const { data: leadInserido, error: errInsert } = await supabase
      .from('leads_carteira' as any)
      .insert(leadRow)
      .select('id')
      .single();
    if (errInsert) {
      console.error('[criarLeadEcossistemaCore] passo 3c INSERT FALHOU:', {
        code: (errInsert as any).code,
        message: errInsert.message,
        details: (errInsert as any).details,
        hint: (errInsert as any).hint,
        row_keys: Object.keys(leadRow),
      });
      throw errInsert;
    }
    leadId = (leadInserido as any).id as string;
  }
  console.info('[criarLeadEcossistemaCore] passo 3 — lead OK', { leadId });

  // 4) Marcar consulta como virou_lead (best-effort)
  if (input.consultaId) {
    try {
      await supabase
        .from('ecossistema_consultas' as any)
        .update({ virou_lead: true, lead_id: leadId })
        .eq('id', input.consultaId);
    } catch (e) {
      console.warn('[Ecossistema] não foi possível marcar virou_lead:', e);
    }
  }

  return { leadId, importacaoId, campanhaId };
}

/**
 * Hook: ponte Ecossistema API → Lead (com toast + navigate).
 *
 * Modo padrão: cria lead, mostra toast verde e navega para /gestao-leads.
 * Modo silent ({ silent: true }): cria lead em background sem UI feedback —
 * usado pelo auto-vira-lead disparado a cada consulta CNPJ no useEcossistemaApi.
 */
export function useCriarLeadDoEcossistema() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CriarLeadInput) => {
      return await criarLeadEcossistemaCore(input);
    },
    onSuccess: (result, input) => {
      qc.invalidateQueries({ queryKey: ['consultas-salvas'] });
      qc.invalidateQueries({ queryKey: ['campanhas-prospeccao-ativas'] });
      qc.invalidateQueries({ queryKey: ['leads-carteira'] });
      qc.invalidateQueries({ queryKey: ['gestao-leads'] });

      if (input.silent) {
        console.info('[Ecossistema] auto-vira-lead OK', {
          leadId: result.leadId,
          campanhaId: result.campanhaId,
          cnpj: input.cnpj,
        });
        return;
      }

      toast({
        title: 'Lead criado com sucesso',
        description: `Lead ${input.razaoSocial} adicionado à campanha do mês.`,
      });
      navigate(`/gestao-leads?lead_id=${result.leadId}`);
    },
    onError: (err: any, input) => {
      if (input?.silent) {
        console.warn('[Ecossistema] auto-vira-lead falhou (silenciado):', err);
        return;
      }
      toast({
        title: 'Erro ao criar lead',
        description: err?.message || 'Erro desconhecido',
        variant: 'destructive',
      });
    },
  });
}
